/**
 * Create a BFF user, in three steps.
 *
 * 1. **Dados de acesso** — name, e-mail and password, with the policy shown as a live checklist.
 * 2. **Papel** — optional; a user with no role starts with no permissions.
 * 3. **Revisar e criar**.
 *
 * The password checklist mirrors the BFF's policy so the person sees which rule is still missing
 * while typing rather than collecting rejections one submit at a time. The server still decides.
 *
 * @module Pages/Settings/UserManagement
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import { UTButton, UTTextField } from '@group-link-one/gl-ui/utilities';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import { createUser, getRoles } from '../../Services';
import type { Role } from '../../Types/UserManagement.types';
import { PASSWORD_RULES, isPasswordValid } from '../../Utils/passwordPolicy';
import { formatRoleName, selectableRoles } from '../../Utils/userPresentation';
import { StepNav } from '@/Shared/components/StepNav/StepNav';
import { MainContent } from '@/Templates/Main/MainContent/MainContent';
import { useActionToast } from '@/Hooks/useActionToast';

const layoutStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-ml);
  align-items: flex-start;
`;

const panelStyle = css`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-xs);
  padding: var(--space-l);
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
  flex: 100 1 340px;
  min-width: 0;
`;

const stepHeaderStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
`;

const stepTitleStyle = css`
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
`;

const stepSubtitleStyle = css`
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const checklistStyle = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: var(--space-m);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
`;

const checklistItemStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

const checklistItemMetStyle = css`
  color: var(--color-success);
  font-weight: var(--font-weight-semibold);
`;

const roleGridStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(240px, 100%), 1fr));
  gap: var(--space-m);
`;

const roleCardStyle = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: var(--space-m);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-m);
  background: var(--color-surface);
  cursor: pointer;
  text-align: left;
  font-family: var(--font-sans);
  transition: border-color var(--duration-fast) var(--ease-standard);

  &:hover {
    border-color: var(--color-border-strong);
  }
`;

/*
  The hover rule on the base class is `.base:hover`, which outranks a plain `.selected` — so right
  after clicking, with the pointer still on the card, the border went back to grey and the card
  read as unselected. The selected state has to win while hovered too.
*/
const roleCardSelectedStyle = css`
  &,
  &:hover {
    border-color: var(--color-primary);
    background: var(--color-primary-subtle);
  }
`;

const roleNameStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const roleSlugStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const roleDescriptionStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const reviewRowStyle = css`
  display: flex;
  justify-content: space-between;
  gap: var(--space-m);
  padding: var(--space-s) 0;
  border-bottom: 1px solid var(--color-divider);
  font-size: var(--font-size-sm);
`;

const reviewLabelStyle = css`
  color: var(--color-text-secondary);
`;

const reviewValueStyle = css`
  font-weight: var(--font-weight-semibold);
  text-align: right;
`;

const errorStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-danger);
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding-top: var(--space-m);
  border-top: 1px solid var(--color-divider);
`;

const spacerStyle = css`
  flex: 1;
`;

const STEPS = [
  { number: 1, title: 'Dados de acesso', hint: 'Nome, e-mail e senha' },
  { number: 2, title: 'Papel', hint: 'Opcional — define as permissões' },
  { number: 3, title: 'Revisar', hint: 'Confere e cria' },
];

export interface UserCreateProps {
  onBack?: () => void;
  onSuccess?: () => void;
}

const UserCreate = ({ onBack, onSuccess }: UserCreateProps): JSX.Element => {
  const toast = useActionToast();
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  /*
    Um olho por campo, e independentes: com a senha à mostra a confirmação ainda serve para
    conferir o que foi digitado nela, e quem revela uma não revela a outra sem querer.
  */
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [roleId, setRoleId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: roles } = useQuery({ queryKey: ['admin', 'roles'], queryFn: getRoles });

  const roleList = useMemo(() => selectableRoles(roles ?? []), [roles]);
  const selectedRole: Role | undefined = roleList.find(role => role.id === roleId);

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const passwordsMatch = password.length > 0 && password === confirmation;
  const canAdvanceFromAccess =
    name.trim().length > 0 && isEmailValid && isPasswordValid(password) && passwordsMatch;

  const handleSubmit = async () => {
    setIsSaving(true);
    setSubmitError(null);

    try {
      await createUser({
        name: name.trim(),
        email: email.trim(),
        password,
        ...(roleId ? { roleIds: [roleId] } : {}),
      });
      toast.success(`Usuário ${name.trim()} criado`);
      onSuccess?.();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Não foi possível criar o usuário.');
      toast.failure('Não foi possível criar o usuário', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <MainContent id='user-create' styling={{ noBackgroundColor: true, noBorder: true }}>
      <div className={layoutStyle}>
        <StepNav steps={STEPS} currentStep={step} />

        <section className={panelStyle}>
          {step === 1 ? (
            <>
              <div className={stepHeaderStyle}>
                <h2 className={stepTitleStyle}>Dados de acesso</h2>
                <p className={stepSubtitleStyle}>Com o que a pessoa vai entrar no BFF Apps.</p>
              </div>

              <UTTextField
                id='user-create-name'
                topLabel='Nome'
                required
                value={name}
                placeholder='Marina Duarte'
                onChange={event => {
                  setName(event.target.value);
                }}
              />

              <UTTextField
                id='user-create-email'
                topLabel='E-mail'
                required
                value={email}
                placeholder='nome@grouplinkone.com'
                state={email.length > 0 && !isEmailValid ? 'error' : 'normal'}
                bottomLabel={email.length > 0 && !isEmailValid ? 'Informe um e-mail válido.' : ''}
                onChange={event => {
                  setEmail(event.target.value);
                }}
              />

              <UTTextField
                id='user-create-password'
                topLabel='Senha'
                required
                type={showPassword ? 'text' : 'password'}
                value={password}
                rightIcon={showPassword ? 'Misc.Eye' : 'Misc.EyeClosed'}
                rightIconAction={() => {
                  setShowPassword(current => !current);
                }}
                onChange={event => {
                  setPassword(event.target.value);
                }}
              />

              <div className={checklistStyle}>
                {PASSWORD_RULES.map(rule => {
                  const met = rule.isMet(password);

                  return (
                    <span
                      key={rule.id}
                      className={`${checklistItemStyle} ${met ? checklistItemMetStyle : ''}`}
                    >
                      <GLIcon
                        variant={met ? 'Communication.CheckCircle' : 'Arrows.X'}
                        size={14}
                        fill={met ? 'var(--color-success)' : 'var(--color-text-muted)'}
                      />
                      {rule.label}
                    </span>
                  );
                })}
              </div>

              <UTTextField
                id='user-create-confirmation'
                topLabel='Confirmar senha'
                required
                type={showConfirmation ? 'text' : 'password'}
                value={confirmation}
                rightIcon={showConfirmation ? 'Misc.Eye' : 'Misc.EyeClosed'}
                rightIconAction={() => {
                  setShowConfirmation(current => !current);
                }}
                state={confirmation.length > 0 && !passwordsMatch ? 'error' : 'normal'}
                bottomLabel={
                  confirmation.length > 0 && !passwordsMatch ? 'As senhas não conferem.' : ''
                }
                onChange={event => {
                  setConfirmation(event.target.value);
                }}
              />
            </>
          ) : null}

          {step === 2 ? (
            <>
              <div className={stepHeaderStyle}>
                <h2 className={stepTitleStyle}>Papel</h2>
                <p className={stepSubtitleStyle}>
                  Opcional. Sem papel, a pessoa entra sem nenhuma permissão e alguém precisa
                  concedê-las depois.
                </p>
              </div>

              <div className={roleGridStyle}>
                {roleList.map(role => {
                  const isSelected = role.id === roleId;

                  return (
                    <button
                      key={role.id}
                      type='button'
                      aria-pressed={isSelected}
                      className={`${roleCardStyle} ${isSelected ? roleCardSelectedStyle : ''}`}
                      onClick={() => {
                        setRoleId(isSelected ? null : role.id);
                      }}
                    >
                      <span className={roleNameStyle}>{formatRoleName(role.name)}</span>
                      <span className={roleSlugStyle}>
                        {role.name} · {role.permissions?.length ?? 0} permissões
                      </span>
                      {role.description ? (
                        <span className={roleDescriptionStyle}>{role.description}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </>
          ) : null}

          {step === 3 ? (
            <>
              <div className={stepHeaderStyle}>
                <h2 className={stepTitleStyle}>Revisar e criar</h2>
                <p className={stepSubtitleStyle}>Confira antes de criar a conta.</p>
              </div>

              <div>
                <div className={reviewRowStyle}>
                  <span className={reviewLabelStyle}>Nome</span>
                  <span className={reviewValueStyle}>{name}</span>
                </div>
                <div className={reviewRowStyle}>
                  <span className={reviewLabelStyle}>E-mail</span>
                  <span className={reviewValueStyle}>{email}</span>
                </div>
                <div className={reviewRowStyle}>
                  <span className={reviewLabelStyle}>Papel</span>
                  <span className={reviewValueStyle}>
                    {selectedRole ? formatRoleName(selectedRole.name) : 'Nenhum'}
                  </span>
                </div>
                <div className={reviewRowStyle}>
                  <span className={reviewLabelStyle}>Permissões</span>
                  <span className={reviewValueStyle}>{selectedRole?.permissions?.length ?? 0}</span>
                </div>
              </div>

              {submitError ? <span className={errorStyle}>{submitError}</span> : null}
            </>
          ) : null}

          <div className={footerStyle}>
            <UTButton
              fitContent
              variant='tertiary'
              text={step === 1 ? 'Cancelar' : 'Voltar'}
              onClick={() => {
                if (step === 1) {
                  onBack?.();
                  return;
                }
                setStep(current => current - 1);
              }}
            />

            <div className={spacerStyle} />

            {step < 3 ? (
              <UTButton
                fitContent
                id='user-create-next'
                text='Continuar'
                disabled={step === 1 && !canAdvanceFromAccess}
                onClick={() => {
                  setStep(current => current + 1);
                }}
              />
            ) : (
              <UTButton
                fitContent
                id='user-create-submit'
                text={isSaving ? 'Criando...' : 'Criar usuário'}
                disabled={!canAdvanceFromAccess || isSaving}
                onClick={() => {
                  void handleSubmit();
                }}
              />
            )}
          </div>
        </section>
      </div>
    </MainContent>
  );
};

export { UserCreate };
